/**
 * Shared utility functions for Order Processing, Tax Calculation, and Outlet Distribution.
 */

/**
 * Normalize SKU keys and merge duplicate quantities.
 */
export const normalizeAndMergeItems = (rawItems) => {
  const merged = {};
  Object.entries(rawItems || {}).forEach(([sku, qty]) => {
    const key = sku.trim().toUpperCase();
    const q = Number(qty || 0);
    if (q > 0) merged[key] = (merged[key] || 0) + q;
  });
  return merged;
};

/**
 * Fair-share distribution of SKU quantities across multiple GST outlets.
 * respects per-invoice limits and remaining annual limits of each outlet.
 */
export const splitOrderToOutlets = (totalQty, minQty, trackers, unitPrice) => {
  const m = Number(minQty) || 0.001;
  const q = Number(totalQty);
  if (q <= 0 || trackers.length === 0) return null;

  const results = new Array(trackers.length).fill(0);
  let remainingQty = q;
  
  // Trackers passed here are [ { id, remainingLimit } ]
  let activeOutlets = trackers.map((t, idx) => ({ idx, rem: t.remainingLimit }));

  // We distribute using a "Fair Share" round-robin style
  while (remainingQty >= (m - 0.000001) && activeOutlets.length > 0) {
    const unitsToDistribute = Math.floor(remainingQty / m);
    if (unitsToDistribute === 0) break;

    const fairShareUnits = Math.floor(unitsToDistribute / activeOutlets.length);

    if (fairShareUnits === 0) {
      // Less than 1 minUnit per outlet left. Distribute 1 by 1.
      for (let i = 0; i < activeOutlets.length && remainingQty >= (m - 0.000001); i++) {
        const o = activeOutlets[i];
        if (o.rem >= (m * unitPrice)) {
          results[o.idx] = Number((results[o.idx] + m).toFixed(4));
          o.rem -= (m * unitPrice);
          remainingQty = Number((remainingQty - m).toFixed(4));
        } else {
          activeOutlets.splice(i, 1);
          i--;
        }
      }
      break; 
    }

    let nextRound = [];
    for (const o of activeOutlets) {
      const fairQty = fairShareUnits * m;
      const fairCost = fairQty * unitPrice;
      
      if (o.rem >= (fairCost - 0.0001)) {
        results[o.idx] = Number((results[o.idx] + fairQty).toFixed(4));
        o.rem -= fairCost;
        remainingQty = Number((remainingQty - fairQty).toFixed(4));
        nextRound.push(o);
      } else {
        const maxUnits = Math.floor(o.rem / (m * unitPrice));
        if (maxUnits > 0) {
          const actualQty = maxUnits * m;
          results[o.idx] = Number((results[o.idx] + actualQty).toFixed(4));
          o.rem -= (actualQty * unitPrice);
          remainingQty = Number((remainingQty - actualQty).toFixed(4));
        }
      }
    }
    
    if (nextRound.length === 0 && remainingQty >= (m - 0.000001)) break;
    activeOutlets = nextRound;
  }

  if (remainingQty > 0.000001) {
    results[0] = Number((results[0] + remainingQty).toFixed(4));
  }

  return results;
};

/**
 * Calculates line totals and tax components for a given SKU and Quantity.
 */
export const calculateLineTotals = (skuData, qty) => {
  const q = Number(qty || 0);
  const rate = Number(skuData.Basic_Eway || 0);
  const basic = q * rate;
  const mrpTotal = q * (Number(skuData.mrp) || 0);
  const gstPct = Number(skuData.gst || 0);
  
  const gstAmount = mrpTotal - (mrpTotal / (1 + (gstPct / 100)));
  const cessAmount = basic * ((Number(skuData.cess) || 0) / 100);
  const addlCessAmount = q * (Number(skuData.addlcess) || 0);
  const rebateAmount = q * (Number(skuData.Rebate) || 0);

  const ewayAmount = basic + gstAmount + cessAmount + addlCessAmount;
  const totalAmount = ewayAmount + rebateAmount;

  return {
    qty: q,
    basicRate: rate,
    basicAmount: basic,
    gstAmount,
    cessAmount,
    addlCessAmount,
    rebateAmount,
    lineTotal: totalAmount,
    ewayLineTotal: ewayAmount,
    gstPercent: gstPct,
    cessPercent: Number(skuData.cess || 0),
    mrp: Number(skuData.mrp || 0),
    rebatePerUnit: Number(skuData.Rebate || 0),
    addlCessPerUnit: Number(skuData.addlcess || 0),
    hsn: String(skuData.HSN || skuData.hsn || skuData.Hsn || "").trim()
  };
};

/**
 * Calculates aggregate totals for an entire order based on an items map.
 */
export const calculateOrderTotals = (items, skuMaster) => {
  let tQty = 0, tBasic = 0, tGST = 0, tCess = 0, tAddl = 0, tRebate = 0, tGrand = 0, tEway = 0;
  
  Object.entries(items || {}).forEach(([sku, qty]) => {
    const q = Number(qty || 0);
    if (q <= 0) return;
    
    const m = skuMaster[sku.trim().toUpperCase()] || skuMaster[sku.trim().toLowerCase()];
    if (!m) return;
    
    const line = calculateLineTotals(m, q);
    tQty += line.qty;
    tBasic += line.basicAmount;
    tGST += line.gstAmount;
    tCess += line.cessAmount;
    tAddl += line.addlCessAmount;
    tRebate += line.rebateAmount;
    tGrand += line.lineTotal;
    tEway += line.ewayLineTotal;
  });
  
  return { tQty, tBasic, tGST, tCess, tAddl, tRebate, tGrand, tEway };
};
